import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type ClassTypeItem = { id: string; name: string };
type LevelItem = { id: string; name: string };
type RoomItem = { id: string; name: string };

export type ClassCapacityReportSearch = {
  dateFrom?: string;
  dateTo?: string;
  classTypeId?: string;
  levelId?: string;
  roomId?: string;
  locationKind?: "studio" | "off_site";
  engagementKind?: "standard" | "partner_instructor_only";
};

export function ClassCapacityReportFilter() {
  const nav = useNavigate({ from: "/app/reports/class-capacity" });
  const search = useSearch({
    from: "/app/reports/class-capacity",
  }) as ClassCapacityReportSearch;

  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");

  const [fromDate, setFromDate] = useState(search.dateFrom ?? defaultFrom);
  const [toDate, setToDate] = useState(search.dateTo ?? defaultTo);
  const [classTypeId, setClassTypeId] = useState(search.classTypeId ?? "");
  const [levelId, setLevelId] = useState(search.levelId ?? "");
  const [roomId, setRoomId] = useState(search.roomId ?? "");
  const [locationFilter, setLocationFilter] = useState<
    "all" | "studio" | "off_site"
  >(search.locationKind ?? "all");
  const [engagementFilter, setEngagementFilter] = useState<
    "all" | "standard" | "partner_instructor_only"
  >(search.engagementKind ?? "all");

  useEffect(() => {
    setFromDate(search.dateFrom ?? defaultFrom);
    setToDate(search.dateTo ?? defaultTo);
    setClassTypeId(search.classTypeId ?? "");
    setLevelId(search.levelId ?? "");
    setRoomId(search.roomId ?? "");
    setLocationFilter(search.locationKind ?? "all");
    setEngagementFilter(search.engagementKind ?? "all");
  }, [
    search.dateFrom,
    search.dateTo,
    search.classTypeId,
    search.levelId,
    search.roomId,
    search.locationKind,
    search.engagementKind,
  ]);

  const hasFilter =
    classTypeId ||
    levelId ||
    roomId ||
    locationFilter !== "all" ||
    engagementFilter !== "all" ||
    search.dateFrom ||
    search.dateTo;

  const applyFilters = (updates: Partial<ClassCapacityReportSearch>) => {
    const next = {
      dateFrom: updates.dateFrom ?? fromDate,
      dateTo: updates.dateTo ?? toDate,
      classTypeId:
        updates.classTypeId !== undefined
          ? updates.classTypeId
          : classTypeId
            ? classTypeId
            : undefined,
      levelId:
        updates.levelId !== undefined
          ? updates.levelId
          : levelId
            ? levelId
            : undefined,
      roomId:
        updates.roomId !== undefined
          ? updates.roomId
          : roomId
            ? roomId
            : undefined,
      locationKind:
        updates.locationKind !== undefined
          ? updates.locationKind
          : locationFilter === "all"
            ? undefined
            : locationFilter,
      engagementKind:
        updates.engagementKind !== undefined
          ? updates.engagementKind
          : engagementFilter === "all"
            ? undefined
            : engagementFilter,
    };
    nav({ search: next });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : defaultFrom;
              const toStr = to ? format(to, "yyyy-MM-dd") : defaultTo;
              setFromDate(fromStr);
              setToDate(toStr);
              applyFilters({ dateFrom: fromStr, dateTo: toStr });
            }}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />

          <InfiniteCombobox<ClassTypeItem>
            queryKey={["master", "class-types", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<ClassTypeItem>(
                `${config.apiUrl}/master/class-types/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<ClassTypeItem>(
                `${config.apiUrl}/master/class-types/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={classTypeId}
            onValueChange={(val) => {
              setClassTypeId(val);
              applyFilters({ classTypeId: val || undefined });
            }}
            placeholder="ປະເພດຄລາດສ໌"
            className="h-8 w-full sm:w-40"
            clearable
          />

          <InfiniteCombobox<LevelItem>
            queryKey={["master", "levels", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<LevelItem>(
                `${config.apiUrl}/master/levels/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<LevelItem>(
                `${config.apiUrl}/master/levels/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={levelId}
            onValueChange={(val) => {
              setLevelId(val);
              applyFilters({ levelId: val || undefined });
            }}
            placeholder="ລະດັບ"
            className="h-8 w-full sm:w-40"
            clearable
          />

          <InfiniteCombobox<RoomItem>
            queryKey={["master", "rooms", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<RoomItem>(
                `${config.apiUrl}/master/rooms/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<RoomItem>(
                `${config.apiUrl}/master/rooms/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={roomId}
            onValueChange={(val) => {
              setRoomId(val);
              applyFilters({ roomId: val || undefined });
            }}
            placeholder="ຫ້ອງ"
            className="h-8 w-full sm:w-40"
            clearable
          />

          <SimpleSelect
            value={locationFilter}
            onValueChange={(val) => {
              const v = val as "all" | "studio" | "off_site";
              setLocationFilter(v);
              applyFilters({
                locationKind: v === "all" ? undefined : v,
              });
            }}
            placeholder="ສະຖານທີ່"
            options={[
              { value: "all", label: "ທຸກສະຖານທີ່" },
              { value: "studio", label: "ສະຕູດິໂອ" },
              { value: "off_site", label: "ນອກສະຖານທີ່" },
            ]}
            className="h-8 w-full sm:w-40"
          />

          <SimpleSelect
            value={engagementFilter}
            onValueChange={(val) => {
              const v = val as "all" | "standard" | "partner_instructor_only";
              setEngagementFilter(v);
              applyFilters({
                engagementKind: v === "all" ? undefined : v,
              });
            }}
            placeholder="ຮູບແບບ"
            options={[
              { value: "all", label: "ທຸກຮູບແບບ" },
              { value: "standard", label: "ມາດຕະຖານ" },
              {
                value: "partner_instructor_only",
                label: "Partner (ຄູຢ່າງດຽວ)",
              },
            ]}
            className="h-8 w-full sm:w-44"
          />
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFromDate(defaultFrom);
              setToDate(defaultTo);
              setClassTypeId("");
              setLevelId("");
              setRoomId("");
              setLocationFilter("all");
              setEngagementFilter("all");
              nav({
                search: {
                  dateFrom: defaultFrom,
                  dateTo: defaultTo,
                  classTypeId: undefined,
                  levelId: undefined,
                  roomId: undefined,
                  locationKind: undefined,
                  engagementKind: undefined,
                },
              });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="size-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
